import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { buildError, prepareHeaders, API } from '../../api/apis';
import { ListFirewallRequest, CreateFirewallRequest, UpdateFirewallRequest, DeleteFirewallRequest, CreateFirewallResponse, ApiResponse, FirewallsDescribe, UpdateFirewallResponse } from '../../interfaces/interfaces';
import { Firewall } from '../../api/FwaasDataTypes';

export const firewallsService = createApi({
  reducerPath: 'firewallsApi',
  baseQuery: fetchBaseQuery({
    prepareHeaders: prepareHeaders,
  }),
  tagTypes: ['Firewall'],
  endpoints: (builder) => ({
    getFirewalls: builder.query({
      queryFn: async (request: ListFirewallRequest, { dispatch, getState }, extraOptions, baseQuery) => {
        // First, get the initial page
        const initialResult = await baseQuery(API.FETCH_FIREWALLS(request));

        if (initialResult.error) {
          return { error: buildError(initialResult.error, "") };
        }

        const initialResponse = initialResult.data as ApiResponse;
        const initialData = initialResponse.Response;

        // Determine which field to read based on describe flag
        const isDescribe = request?.Describe === true;
        let allFirewalls: FirewallsDescribe[] = isDescribe
          ? (initialData?.FirewallsDescribe || [])
          : (initialData?.Firewalls || []);

        let nextToken = initialData?.NextToken;
        let hasMore = !!nextToken;

        // Return initial data immediately
        const finalResult = {
          data: {
            Firewalls: allFirewalls,
            NextToken: nextToken,
          }
        };

        // If there's more data, start background fetching
        if (hasMore) {
          // Use setTimeout to allow the initial data to be cached first
          setTimeout(async () => {
            while (hasMore) {
              try {
                const currentRequest = {
                  ...request,
                  NextToken: nextToken,
                  MaxResults: request?.MaxResults || 25
                };
                const result = await baseQuery(API.FETCH_FIREWALLS(currentRequest));

                if (result.error) {
                  console.error('Error fetching next page:', result.error);
                  return { error: buildError(initialResult.error, "") };
                  break;
                }

                const response = result.data as ApiResponse;
                const firewallData = response.Response;

                // Read the appropriate field based on describe flag
                const newFirewalls = isDescribe
                  ? (firewallData?.FirewallsDescribe || [])
                  : (firewallData?.Firewalls || []);

                  allFirewalls = [...allFirewalls, ...newFirewalls];

                  // Update cache with new data
                  dispatch(
                    firewallsService.util.updateQueryData('getFirewalls', request, (draft) => {
                      draft.Firewalls = allFirewalls;
                      draft.NextToken = firewallData.NextToken;
                    })
                  );

                nextToken = firewallData?.NextToken;
                hasMore = !!nextToken;

                // Add delay to see incremental updates and avoid overwhelming the API
                if (hasMore) {
                  await new Promise(resolve => setTimeout(resolve, 500));
                }

              } catch (error) {
                console.error('Error fetching next page of firewalls:', error);
                break;
              }
            }
          }, 0);
        }

        return finalResult;
      },
      providesTags: () => [{ type: 'Firewall', id: 'LIST' }],
    }),
    getFirewallById: builder.query({
      query: ({ fwid, region }: { fwid: string, region: string }) => API.FETCH_FIREWALL_BY_ID(fwid, region),
      providesTags: () => [{ type: 'Firewall', id: 'DESCRIBE' }],
      transformResponse: (response: ApiResponse) => {
        return response.Response as FirewallsDescribe[];
      },
      transformErrorResponse: (response) => {
        return buildError(response, "");
      },
    }),
    createFirewall: builder.mutation({
      query: ({ newFirewallDetails, region }: { newFirewallDetails: CreateFirewallRequest, region: string }) => {
        return {
          url: `${API.CREATE_FIREWALL(region)}`,
          method: 'POST',
          body: newFirewallDetails,
        };
      },
      transformResponse: (response: ApiResponse) => {
        return response.Response as CreateFirewallResponse;
      },
      transformErrorResponse: (response) => {
        return buildError(response, "");
      },
      async onQueryStarted({ newFirewallDetails, region }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;

          // Try to update existing cache entries
          const state = getState();
          const queries = state.firewallsApi.queries;

          // Look for any getFirewalls cache entries
          let updated = false;

          for (const [key, query] of Object.entries(queries)) {
            if (key.includes('getFirewalls') && query?.data) {
              try {
                dispatch(
                  firewallsService.util.updateQueryData('getFirewalls', query.originalArgs || {}, (draft) => {
                    if (draft && draft.Firewalls) {
                      const dataWithStatus = {Firewall: {...data}, Status: 'CREATING'};
                      draft.Firewalls.unshift(dataWithStatus);
                      updated = true;
                    }
                  })
                );
                break; // Exit after first successful update
              } catch (e) {
                console.warn('Cache update failed for key:', key);
                continue;
              }
            }
          }

          // If no cache was updated, invalidate tags to trigger refetch
          if (!updated) {
            dispatch(firewallsService.util.invalidateTags([{ type: 'Firewall', id: 'LIST' }]));
          }

        } catch (error) {
          console.error('Create firewall failed:', error);
          // Invalidate on error to ensure consistency
          dispatch(firewallsService.util.invalidateTags([{ type: 'Firewall', id: 'LIST' }]));
        }
      },
      invalidatesTags: [{ type: 'Firewall', id: 'LIST' }]
    }),
    updateFirewall: builder.mutation({
      query: ({ payload }: { payload: UpdateFirewallRequest }) => ({
        url: `${API.UPDATE_FIREWALL(payload)}`,
        method: 'PATCH',
        body: payload,
      }),
      transformResponse: (response: ApiResponse) => {
        return response.Response as UpdateFirewallResponse;
      },
      transformErrorResponse: (response) => {
        return buildError(response, "");
      },
      async onQueryStarted({ payload }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;

          // Try to update existing cache entries
          const state = getState();
          const queries = state.firewallsApi.queries;

          // Look for any getFirewalls cache entries
          let updated = false;

          for (const [key, query] of Object.entries(queries)) {
            if (key.includes('getFirewalls') && query?.data) {
              try {
                dispatch(
                  firewallsService.util.updateQueryData('getFirewalls', query.originalArgs || {}, (draft) => {
                    if (draft && draft.items) {
                      const firewallIndex = draft.items.findIndex(firewall =>
                        firewall.FirewallId === payload.FirewallId
                      );
                      if (firewallIndex !== -1) {
                        // Update the existing firewall with the response data
                        draft.items[firewallIndex] = { ...draft.items[firewallIndex], ...data };
                        updated = true;
                      }
                    }
                  })
                );
                break; // Exit after first successful update
              } catch (e) {
                console.warn('Cache update failed for key:', key);
                continue;
              }
            }
          }

          // If no cache was updated, invalidate tags to trigger refetch
          if (!updated) {
            dispatch(firewallsService.util.invalidateTags([{ type: 'Firewall', id: 'LIST' }, { type: 'Firewall', id: 'DESCRIBE' }]));
          }

        } catch (error) {
          console.error('Update firewall failed:', error);
          // Invalidate on error to ensure consistency
          dispatch(firewallsService.util.invalidateTags([{ type: 'Firewall', id: 'LIST' }, { type: 'Firewall', id: 'DESCRIBE' }]));
        }
      },
      invalidatesTags: [{ type: 'Firewall', id: 'DESCRIBE' }, { type: 'Firewall', id: 'LIST' }]
    }),
    deleteFirewall: builder.mutation({
      query: ({ payload }: { payload: DeleteFirewallRequest }) => ({
        url: `${API.DELETE_FIREWALL(payload)}`,
        method: 'DELETE',
        body: { Force: payload.Force ?? false },
      }),
      async onQueryStarted({ payload }, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;

          // Try to update existing cache entries
          const state = getState();
          const queries = state.firewallsApi.queries;

          // Look for any getFirewalls cache entries
          let updated = false;

          for (const [key, query] of Object.entries(queries)) {
            if (key.includes('getFirewalls') && query?.data) {
              try {
                dispatch(
                  firewallsService.util.updateQueryData('getFirewalls', query.originalArgs || {}, (draft) => {
                    if (draft && draft.items) {
                      // Remove the deleted firewall
                      const initialLength = draft.items.length;
                      draft.items = draft.items.filter(firewall =>
                        firewall.FirewallId !== payload.FirewallId
                      );
                      // Update count if firewall was actually removed
                      if (draft.items.length < initialLength) {
                        updated = true;
                      }
                    }
                  })
                );
                break; // Exit after first successful update
              } catch (e) {
                console.warn('Cache update failed for key:', key);
                continue;
              }
            }
          }

          // If no cache was updated, invalidate tags to trigger refetch
          if (!updated) {
            dispatch(firewallsService.util.invalidateTags([{ type: 'Firewall', id: 'LIST' }]));
          }

        } catch (error) {
          console.error('Delete firewall failed:', error);
          // Invalidate on error to ensure consistency
          dispatch(firewallsService.util.invalidateTags([{ type: 'Firewall', id: 'LIST' }]));
        }
      },
      invalidatesTags: [{ type: 'Firewall', id: 'LIST' }],
      transformResponse: (response: ApiResponse) => {
        return response.Response as any;
      },
      transformErrorResponse: (response) => {
        return buildError(response, "");
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetFirewallsQuery, useCreateFirewallMutation,
  useGetFirewallByIdQuery, useLazyGetFirewallByIdQuery, useUpdateFirewallMutation, useDeleteFirewallMutation
} = firewallsService;
